/**
 * Companies API — mirrors `cargoCompanies.js` mounted at /api/cargo/me/company.
 * Owner-side endpoints; admin-side is in carwhats-web's AdminCargoCompaniesTab.
 */
import { api } from './client';
import { unwrap } from './_unwrap';
import type { Company, CompanyKycDoc, CompanyMember, CompanyType, CompanyOperatorMode, KycDocKind } from '@/types/company';
import type {
  PricingPreset, CreatePricingPresetInput,
  CompanyReview, CompanyCustomer, CompanyCustomerDetail,
  MoneySummary, UpdateOperatorModeInput,
} from '@/types/companyExtras';

export interface CreateCompanyInput {
  companyType: CompanyType;
  operatorMode: CompanyOperatorMode;
  name: string;
  nameAr?: string;
  commercialRegister?: string;
  taxId?: string;
  importLicense?: string;
  logoUrl?: string;
  country?: string;
  city?: string;
  address?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export const companiesApi = {
  /** Returns the current user's active company (one per owner) — null if none. */
  async getMyCompany(): Promise<(Company & { kyc_docs?: CompanyKycDoc[] }) | null> {
    try {
      const r = await api.get(`/api/cargo/me/company`);
      return unwrap(r) as Company | null;
    } catch (e: any) {
      if (e?.status === 404) return null;
      throw e;
    }
  },
  async create(input: CreateCompanyInput): Promise<Company> {
    const r = await api.post(`/api/cargo/me/company`, input);
    return unwrap<Company>(r);
  },
  async update(id: number, input: Partial<CreateCompanyInput>): Promise<Company> {
    const r = await api.put(`/api/cargo/me/company/${id}`, input);
    return unwrap<Company>(r);
  },
  async submitForReview(id: number): Promise<Company> {
    const r = await api.post(`/api/cargo/me/company/${id}/submit`);
    return unwrap<Company>(r);
  },
  async uploadKycDoc(id: number, payload: { kind: KycDocKind; fileUrl: string; notes?: string }): Promise<CompanyKycDoc> {
    const r = await api.post(`/api/cargo/me/company/${id}/kyc-docs`, payload);
    return unwrap<CompanyKycDoc>(r);
  },

  async listMembers(): Promise<CompanyMember[]> {
    const r = await api.get(`/api/cargo/me/company/members`);
    return unwrap<CompanyMember[]>(r);
  },
  async inviteMember(payload: { phone: string; role: CompanyMember['role'] }): Promise<CompanyMember> {
    const r = await api.post(`/api/cargo/me/company/members`, payload);
    return unwrap<CompanyMember>(r);
  },
  async removeMember(memberId: number): Promise<void> {
    await api.delete(`/api/cargo/me/company/members/${memberId}`);
  },

  // ─── Sub-mode (operator_mode) update — re-uses PUT /me/company/:id ──
  async updateOperatorMode(id: number, input: UpdateOperatorModeInput): Promise<Company> {
    const r = await api.put(`/api/cargo/me/company/${id}`, { operatorMode: input.operatorMode });
    return unwrap<Company>(r);
  },

  // ─── Pricing presets ───────────────────────────────────────────────
  async listPricingPresets(): Promise<PricingPreset[]> {
    const r = await api.get(`/api/cargo/me/company/pricing-presets`);
    return unwrap<PricingPreset[]>(r);
  },
  async createPricingPreset(input: CreatePricingPresetInput): Promise<PricingPreset> {
    const r = await api.post(`/api/cargo/me/company/pricing-presets`, input);
    return unwrap<PricingPreset>(r);
  },
  async archivePricingPreset(presetId: number): Promise<void> {
    await api.delete(`/api/cargo/me/company/pricing-presets/${presetId}`);
  },

  // ─── Reviews ────────────────────────────────────────────────────────
  async listReviews(): Promise<CompanyReview[]> {
    const r = await api.get(`/api/cargo/me/company/reviews`);
    return unwrap<CompanyReview[]>(r);
  },

  // ─── Customers ──────────────────────────────────────────────────────
  async listCustomers(): Promise<CompanyCustomer[]> {
    const r = await api.get(`/api/cargo/me/company/customers`);
    return unwrap<CompanyCustomer[]>(r);
  },
  async getCustomerDetail(customerUserId: string): Promise<CompanyCustomerDetail> {
    const r = await api.get(`/api/cargo/me/company/customers/${encodeURIComponent(customerUserId)}`);
    return unwrap<CompanyCustomerDetail>(r);
  },

  // ─── Money / financial summary (owner-only) ─────────────────────────
  async getMoneySummary(params?: { from?: string; to?: string; currency?: string }): Promise<MoneySummary> {
    const r = await api.get(`/api/cargo/me/company/money/summary`, { params });
    return unwrap<MoneySummary>(r);
  },
};
